import {Instant, LocalDateTime} from '@js-joda/core';
import {InstantRange} from '../../app-interfaces/instant-range';
import {DateTimePickerValue} from './date-time-picker/date-time-picker.models';
import {TimeZoneUtil} from '../../utils/time-zone-util';
import {ZonedDateTimeUtil} from '../../utils/zoned-date-time.util';
import {LocalDateTimeUtil} from '../../utils/date-time/local-date-time.util';

/** Преобразование точных периодов в местное время календаря и обратно в зоне браузера. */
export class InstantRangeMapper {
  /** С начала текущего месяца до конца сегодня в зоне браузера. */
  public static currentMonthToToday(): InstantRange {
    const date = ZonedDateTimeUtil.now().toLocalDate();
    return this.fromPicker({
      start: LocalDateTimeUtil.startOfDay(date.withDayOfMonth(1)),
      end: LocalDateTimeUtil.endOfDay(date),
    }, null);
  }

  /** Точные моменты без промежуточного местного времени: сохраняет позднее вхождение часа DST. */
  public static fromInstants(start: Instant, end: Instant): InstantRange {
    return {
      start: start,
      end: end,
    };
  }

  /** День указанного момента в зоне браузера; ISO без смещения трактуется как местное время. */
  public static forTimestamp(timestamp: string): InstantRange {
    const instant = ZonedDateTimeUtil.parseBrowserTimestamp(timestamp);
    const date = ZonedDateTimeUtil.instantToLocalDateTime(instant, TimeZoneUtil.BROWSER_ZONE_ID).toLocalDate();
    return this.fromPicker({
      start: LocalDateTimeUtil.startOfDay(date),
      end: LocalDateTimeUtil.endOfDay(date),
    }, null);
  }

  /** Сегодня в зоне браузера с миллисекундной точностью границ. */
  public static today(): InstantRange {
    const date = ZonedDateTimeUtil.now().toLocalDate();
    return this.fromPicker({
      start: LocalDateTimeUtil.startOfDay(date),
      end: LocalDateTimeUtil.endOfDay(date),
    }, null);
  }

  public static toPicker(value: InstantRange | null | undefined): DateTimePickerValue | null {
    return value ? {start: this.toLocal(value.start), end: this.toLocal(value.end)} : null;
  }

  public static fromPicker(value: DateTimePickerValue, previous: InstantRange | null): InstantRange {
    return {
      start: this.toInstant(value.start, previous?.start),
      end: this.toInstant(value.end, previous?.end),
    };
  }

  private static toLocal(value: Instant): LocalDateTime {
    // Старый popup выбирал время браузера; timeZone использовался отдельно для подписи кнопки.
    return ZonedDateTimeUtil.instantToLocalDateTime(
      value,
      TimeZoneUtil.BROWSER_ZONE_ID,
    );
  }

  private static toInstant(value: LocalDateTime, previous: Instant | undefined): Instant {
    // Сохраняем исходный момент, включая повторяющийся час DST, если границу не изменяли.
    if (previous && value.equals(this.toLocal(previous))) {
      return previous;
    }
    const instant = ZonedDateTimeUtil.localDateTimeToInstant(value, TimeZoneUtil.BROWSER_ZONE_ID);
    return instant;
  }
}
